/**
 * Per-page-visit engagement signals: scroll depth and "where did they stop /
 * how long were they here" — mounted once for the whole app from
 * gatsby-browser.js (onRouteUpdate / pagehide), not per template.
 *
 * Mirrors the rAF-throttled scroll style of
 * src/components/BlockchainSolutions/useBlockchainMotion.js and the
 * IntersectionObserver style of src/hooks/useScrollReveal.js.
 */
import { trackEvent } from './tracking';

const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];

let startedAt = 0;
let maxScrollPercent = 0;
let firedThresholds = new Set();
let lastSectionId = null;
let sectionsSeen = new Set();
let observer = null;
let raf = null;

function computeScrollPercent() {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return 100;
  const pct = ((window.scrollY || doc.scrollTop) / scrollable) * 100;
  return Math.min(100, Math.max(0, Math.round(pct)));
}

function onScroll() {
  if (raf) return;
  raf = requestAnimationFrame(() => {
    raf = null;
    const pct = computeScrollPercent();
    if (pct > maxScrollPercent) maxScrollPercent = pct;
    SCROLL_THRESHOLDS.forEach((threshold) => {
      if (pct >= threshold && !firedThresholds.has(threshold)) {
        firedThresholds.add(threshold);
        trackEvent('scroll_depth', { percent: threshold, page_path: window.location.pathname });
      }
    });
  });
}

function onIntersect(entries) {
  entries.forEach((entry) => {
    if (entry.isIntersecting && entry.target.id) {
      lastSectionId = entry.target.id;
      sectionsSeen.add(entry.target.id);
    }
  });
}

/** Call once a new page is in the DOM (after a route change, or on first load). */
export function beginPageTracking() {
  startedAt = Date.now();
  maxScrollPercent = 0;
  firedThresholds = new Set();
  lastSectionId = null;
  sectionsSeen = new Set();

  if (typeof window === 'undefined') return;
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(onIntersect, { threshold: 0.5 });
    // Scoped to ided elements inside <main> so shared header/footer/popup
    // chrome never pollutes "which section did they stop at".
    document.querySelectorAll('main [id]').forEach((el) => observer.observe(el));
  }
}

/** Call right before leaving a page (route change, pagehide, tab hidden). */
export function endPageTracking() {
  if (typeof window === 'undefined') return;
  window.removeEventListener('scroll', onScroll);
  if (raf) {
    cancelAnimationFrame(raf);
    raf = null;
  }
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (!startedAt) return;
  trackEvent('page_engagement', {
    page_path: window.location.pathname,
    seconds_on_page: Math.round((Date.now() - startedAt) / 1000),
    max_scroll_percent: maxScrollPercent,
    last_section_id: lastSectionId,
    sections_seen: sectionsSeen.size,
  });
  startedAt = 0;
}
